import {
  ToastProvider as CoreUIToastProvider,
  toast as coreUIToast,
  type ToastOptions,
} from '@eleo-rh/core-ui/contexts'
import axios from 'axios'
import { useI18n } from 'context/I18nProvider'
import { type ComponentProps, createContext, type PropsWithChildren, useContext, useMemo } from 'react'

export interface ApiErrorResponse {
  title?: string
  message?: string
  detail?: string
  'hydra:description'?: string
  violations?: Array<{
    propertyPath?: string
    message?: string
  }>
}

export type ToastOverrides = Partial<Omit<ToastOptions, 'variant'>>
type ToastResult = ReturnType<typeof coreUIToast>

type ToastContextValue = ((options: ToastOptions) => ToastResult) &
  Omit<typeof coreUIToast, 'error' | 'success'> & {
    error: (error?: unknown, options?: ToastOverrides) => ToastResult
    success: (options?: ToastOverrides) => ToastResult
  }

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

type ToastProviderProps = PropsWithChildren<ComponentProps<typeof CoreUIToastProvider>>

const ToastProvider = ({ children, position = 'top-right', ...providerProps }: ToastProviderProps) => {
  const { i18n } = useI18n()
  const toast = useMemo<ToastContextValue>(() => {
    const error: ToastContextValue['error'] = (error, options = {}) => {
      const responseData = axios.isAxiosError<ApiErrorResponse>(error) ? error.response?.data : undefined
      const violationMessages = responseData?.violations?.filter(violation => violation.message) ?? []
      const { title, description, ...toastOptions } = options
      const errorDescription =
        violationMessages.length > 1 ? (
          <div className="whitespace-pre-line">
            {violationMessages.map(violation => `• ${violation.message}`).join('\n')}
          </div>
        ) : (
          (violationMessages[0]?.message ??
          responseData?.message ??
          responseData?.detail ??
          responseData?.['hydra:description'] ??
          (error instanceof Error ? error.message : typeof error === 'string' ? error : i18n('internal.error')))
        )

      return coreUIToast.error({
        ...toastOptions,
        title: title ?? responseData?.title ?? i18n('label.error'),
        description: description ?? errorDescription,
      })
    }
    const success: ToastContextValue['success'] = (options = {}) => {
      const { title, ...toastOptions } = options

      return coreUIToast.success({
        ...toastOptions,
        title: title ?? i18n('notification.generic.success'),
      })
    }

    return Object.assign((options: ToastOptions) => coreUIToast(options), coreUIToast, { error, success })
  }, [i18n])

  return (
    <ToastContext.Provider value={toast}>
      <CoreUIToastProvider position={position} {...providerProps} />
      {children}
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)

  if (!context) throw new Error('useToast must be used within a ToastProvider')

  return context
}

export default ToastProvider
